import React, { useState, useEffect, useContext } from "react";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogActions,
    Button,
    Input,
    Checkbox,
    RadioGroup,
    Radio,
    Field,
    Badge,
    Tooltip,
    Text,
    Divider,
    Textarea,
    Combobox,
    Option,
    Spinner,
} from "@fluentui/react-components";
import { Edit20Regular, Delete20Regular, Add20Regular, Dismiss20Regular, Dismiss24Regular, Delete24Regular } from "@fluentui/react-icons";
import { IProject, IUser } from "../types/types";
import { ApiContext } from "../providers/ApiProvider";

interface IConfigureProjectDialogTypes {
    user: IUser;
    projectName: string | undefined | null;
    projectId: string | undefined;
    jwtoken: string;
    selected: boolean;
    clearStates: () => void;
    setMessage: (msg: string) => void;
    setSuccess: (success: boolean) => void;
}

export default function ConfigureProjectDialog(props: IConfigureProjectDialogTypes) {
    const { getProjectById, updateProjectSettings, deleteProject, getUsers } = useContext(ApiContext);
    const [disabled, setDisabled] = useState<boolean>(true);
    const [projectName, setProjectName] = useState<string | undefined>();
    const [popup, setPopup] = useState<boolean>(false);
    const [projectSettings, setProjectSettings] = useState<IProject | null>(null);
    const [isPrivate, setIsPrivate] = useState<boolean>(true);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [locked, setLocked] = useState<boolean | undefined | "mixed">(false);
    const [projectLocked, setProjectLocked] = useState<boolean>(false); // Different from entry_locked
    const [tags, setTags] = useState<string[]>(["Add"]);
    const [memberList, setMemberList] = useState<string[]>([]);
    const [adminList, setAdminList] = useState<string[]>([]);
    const [input, setInput] = useState("");
    const [alwaysOpen, setAlwaysOpen] = useState<boolean>(true);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [budgetTotal, setBudgetTotal] = useState<number | null>(null);
    const [budgetUsed, setBudgetUsed] = useState<number | null>(null);
    const [projectId, setProjectId] = useState<string | undefined>(undefined);
    const [description, setDescription] = useState<string>("");
    const [users, setUsers] = useState<IUser[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
    const [adminInput, setAdminInput] = useState<string>("");

    // Load users when dialog opens (for admin selection)
    useEffect(() => {
        if (popup && props.user.is_admin) {
            loadUsers();
        }
    }, [popup, props.user.is_admin]);

    const loadUsers = async () => {
        try {
            setIsLoadingUsers(true);
            const usersList = await getUsers(props.jwtoken);
            setUsers(usersList);
        } catch (error) {
            console.error("Error loading users:", error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    useEffect(() => {
        if (projectSettings !== null) {
            const _p = projectSettings as any; // Use any to access fields not in IProject interface
            
            // Handle active dates - check if active exists and has from/to properties
            if (_p.active && _p.active.from !== undefined && _p.active.to !== undefined) {
                const activeFrom = _p.active.from;
                const activeTo = _p.active.to;
                if (activeTo === null && activeFrom === null) {
                    setAlwaysOpen(true);
                } else {
                    setAlwaysOpen(false);
                    if (activeFrom !== null) setStartDate(new Date(activeFrom));
                    if (activeTo !== null) setEndDate(new Date(activeTo));
                }
            } else {
                // If active is missing, default to always open
                setAlwaysOpen(true);
                setStartDate(undefined);
                setEndDate(undefined);
            }

            setLocked(_p.entry_locked || false);
            setProjectLocked(_p.locked || false); // Different from entry_locked
            setIsPrivate(_p.private || false);
            setTags(_p.tags || []);
            setMemberList(_p.members || []);
            setAdminList(_p.admins || []);
            // Prefer backend note field, fall back to any legacy description
            setDescription(_p.note || _p.description || "");
            if (_p.budget) {
                setBudgetUsed(_p.budget.used);
                setBudgetTotal(_p.budget.total);
            }
        }
    }, [projectSettings]);

    useEffect(() => {
        if (props.projectName) setProjectName(props.projectName);
    }, [props.projectName]);

    useEffect(() => {
        if (props.projectId) setProjectId(props.projectId);
    }, [props.projectId]);

    useEffect(() => {
        setDisabled(!props.selected);
    }, [props.selected]);

    const handleProjectEdit = async () => {
        try {
            const settings = await getProjectById(props.jwtoken, props.projectId);
            setProjectSettings(settings);
            setPopup(true);
        } catch (error) {
            console.error(error);
            setPopup(false);
        }
    };

    const canEditNote = React.useMemo(() => {
        if (!projectSettings) return false;
        const isAdmin = props.user.is_admin;
        const isProjectManager = props.user.is_project_manager;
        const isPrivateProject = (projectSettings as any)?.private === true;
        const members: string[] = (projectSettings as any)?.members || [];
        const isMember = Array.isArray(members) && members.includes(props.user.oid);
        return isAdmin || isProjectManager || (isPrivateProject && isMember);
    }, [projectSettings, props.user]);

    const handleSubmit = async () => {
        try {
            if (projectSettings !== null) {
                // Create a copy of projectSettings to avoid mutating the original
                // Preserve all original fields, especially company_id which should NEVER change
                // Use 'any' type to include fields not in IProject interface (locked, description, name)
                const body: any = {
                    ...projectSettings,
                    // Only update fields that the user can modify
                    project_name: projectName ? projectName : projectSettings.project_name,
                    entry_locked: locked ? true : false,
                    locked: projectLocked, // Different from entry_locked
                    tags: tags,
                    private: isPrivate,
                    members: memberList,
                    admins: adminList, // Update admins list
                    note: description, // Persist note/description to backend "note" field
                    // Preserve company_id - DO NOT CHANGE IT
                    company_id: projectSettings.company_id,
                    // Preserve owner_oid - don't change ownership
                    owner_oid: projectSettings.owner_oid,
                    // Handle active dates
                    active: {
                        from: null,
                        to: null,
                    },
                    // Handle budget - preserve used value from original (it's calculated by backend)
                    budget: {
                        total: budgetTotal !== null ? budgetTotal : (projectSettings.budget?.total || null),
                        used: projectSettings.budget?.used !== null && projectSettings.budget?.used !== undefined 
                            ? projectSettings.budget.used 
                            : null,
                    },
                };
                
                // Set active dates based on user input
                if (!alwaysOpen && !locked) {
                    body.active.from = startDate ? startDate.valueOf() : null;
                    body.active.to = endDate ? endDate.valueOf() : null;
                } else {
                    // If always open or locked, set to null
                    body.active.from = null;
                    body.active.to = null;
                }

                // Update budget total if user changed it, but preserve used (read-only)
                body.budget.total = budgetTotal !== null ? budgetTotal : (projectSettings.budget?.total || null);
                // budget.used is read-only - always preserve original value
                body.budget.used = projectSettings.budget?.used !== null && projectSettings.budget?.used !== undefined 
                    ? projectSettings.budget.used 
                    : null;

                await updateProjectSettings(props.jwtoken, body, projectId)
                    .then((updatedProject) => {
                        props.setSuccess(true);
                        props.setMessage("Project settings updated successfully");
                        // Refresh the project list to get updated entry_locked status
                        props.clearStates();
                    })
                    .catch((err) => {
                        props.setSuccess(false);
                        props.setMessage(err.message);
                    });
            } else {
                throw new Error("Project settings is null");
            }
        } catch (error) {
            console.error("Error in handleSubmit:", error);
        }
        setPopup(false);
    };

    const handleAddTag = (e: { preventDefault: () => void }) => {
        e.preventDefault();
        const trimmedInput = input.trim();
        setTags((prevState) => [...prevState, trimmedInput]);
        setInput("");
    };
    const handleDeleteProject = () => {
        setShowAlert(true);
    };

    const handleAlertCancel = () => {
        setShowAlert(false);
    };

    const handleAlertConfirm = () => {
        setShowAlert(false);
        // Delete project
        deleteProject(props.jwtoken, projectId)
            .then(() => {
                props.setSuccess(true);
                // if (typeof ret.message === "string") props.setMessage(ret.message);
                // else
                props.setMessage("Project deleted successfully");
                setPopup(false);
                setProjectName(undefined);
                props.clearStates();
            })
            .catch((err) => {
                props.setSuccess(false);
                props.setMessage(err.message);
                setPopup(false);
            });
    };

    return (
        <>
            <Button 
                disabled={disabled} 
                key="editCompanyNameButton" 
                icon={<Edit20Regular />} 
                id="1" 
                onClick={handleProjectEdit}
                className="w-full sm:w-auto"
            >
                Edit
            </Button>
            <Dialog open={popup} modalType="modal" onOpenChange={(_, data) => setPopup(!!data.open)}>
                <DialogSurface className="min-w-[90%] sm:min-w-[500px] md:min-w-[600px] max-w-[800px] max-h-[90vh]">
                    <DialogBody className="flex flex-col overflow-hidden">
                        <DialogTitle className="flex items-center justify-between gap-4 pb-2">
                            <span>Configure project settings</span>
                            {projectSettings?.private && (
                                <Tooltip content="Delete project. (Only available for private projects)" relationship="description">
                                    <Button
                                        disabled={!isPrivate || !projectSettings?.private}
                                        appearance="secondary"
                                        id="button_delete"
                                        type="button"
                                        size="small"
                                        icon={<Delete20Regular />}
                                        onClick={handleDeleteProject}
                                        aria-label="Delete project"
                                    />
                                </Tooltip>
                            )}
                        </DialogTitle>
                        <DialogContent className="flex-1 overflow-y-auto px-4 sm:px-6">
                            <form
                                className="text-(--colorNeutralForeground1) flex flex-col gap-6"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    handleSubmit();
                                }}
                                onReset={() => {
                                    setPopup(false);
                                }}
                            >
                                {/* Project Name Section */}
                                <Field label="Project Name" required>
                                    <Input
                                        className={`w-full text-base sm:text-lg font-semibold ${
                                            !isPrivate ? "text-gray-400" : "text-red-600"
                                        }`}
                                        autoComplete="off"
                                        type="text"
                                        placeholder="Enter project name"
                                        id={`${projectName}-id`}
                                        value={projectName || ""}
                                        onChange={(e) => {
                                            const target = e.target as HTMLInputElement;
                                            setProjectName(target.value);
                                        }}
                                        disabled={!isPrivate}
                                    />
                                </Field>
                                {props.user.is_admin || props.user.is_project_manager ? (
                                    <>
                                        {/* Project Name (internal) and Description */}
                                <div className="flex flex-col gap-4">
                                            <Text weight="semibold" size={500}>
                                                Additional Information
                                            </Text>
                                            
                                            <Field label="Description / Notes">
                                                <Textarea
                                                    className="w-full"
                                                    placeholder="Enter project description"
                                                    value={description}
                                                    onChange={(e) => {
                                                        const target = e.target as HTMLTextAreaElement;
                                                        setDescription(target.value);
                                                    }}
                                                    disabled={disabled || !canEditNote}
                                                    rows={3}
                                                />
                                            </Field>
                                        </div>
                                        
                                        <Divider />
                                        
                                        {/* Active Hours / Locked State Section */}
                                        <div className="flex flex-col gap-4">
                                            <Text weight="semibold" size={500}>
                                                Active Hours & Locked State
                                            </Text>
                                            
                                            <div className="flex flex-col gap-3">
                                                <Checkbox
                                                    label="Entry locked"
                                                    onChange={(e, data) => {
                                                        setLocked(data.checked === true);
                                                        setAlwaysOpen(false);
                                                    }}
                                                    disabled={disabled || !props.user.is_admin}
                                                    checked={Boolean(locked)}
                                                />
                                                
                                                <Checkbox
                                                    label="Project locked"
                                                    onChange={(e, data) => {
                                                        setProjectLocked(data.checked === true);
                                                    }}
                                                    disabled={disabled || !props.user.is_admin}
                                                    checked={Boolean(projectLocked)}
                                                />
                                                
                                                {!locked && (
                                                    <Checkbox
                                                        label="Always open"
                                                        onChange={(e, data) => {
                                                            setAlwaysOpen(data.checked === true);
                                                        }}
                                                        disabled={disabled || !props.user.is_admin}
                                                        checked={Boolean(alwaysOpen)}
                                                    />
                                                )}
                                                
                                                {!locked && !alwaysOpen && (
                                                    <div className="flex flex-col sm:flex-row gap-4 mt-2">
                                                        <Field label="Start Date" className="flex-1">
                                                            <Input
                                                                type="date"
                                                                disabled={!!locked || !!alwaysOpen || !props.user.is_admin}
                                                                value={startDate ? startDate.toISOString().split("T")[0] : ""}
                                                                onChange={(e) => {
                                                                    if (e.target.value) setStartDate(new Date(e.target.value));
                                                                }}
                                                                aria-label="Select start date"
                                                                className="w-full"
                                                            />
                                                        </Field>
                                                        <Field label="End Date" className="flex-1">
                                                            <Input
                                                                type="date"
                                                                disabled={!!locked || !!alwaysOpen || !props.user.is_admin}
                                                                value={endDate ? endDate.toISOString().split("T")[0] : ""}
                                                                onChange={(e) => {
                                                                    if (e.target.value) setEndDate(new Date(e.target.value));
                                                                }}
                                                                aria-label="Select end date"
                                                                className="w-full"
                                                            />
                                                        </Field>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <Divider />
                                        
                                        {/* Visibility Section */}
                                        {props.user.is_admin && (
                                            <div className="flex flex-col gap-3">
                                                <Text weight="semibold" size={500}>
                                                    Visibility
                                                </Text>
                                                <RadioGroup
                                                    layout="horizontal"
                                                    value={isPrivate ? "private" : "public"}
                                                    onChange={(_, p) => {
                                                        const members: string[] = [];
                                                        if (p.value === "public") {
                                                            members.push("offcodeGroup");
                                                            setIsPrivate(false);
                                                        } else {
                                                            members.push(props.user.oid);
                                                            setIsPrivate(true);
                                                        }
                                                        setMemberList(members);
                                                    }}
                                                >
                                                    <Radio value="private" label="Private" />
                                                    <Radio value="public" label="Public" />
                                                </RadioGroup>
                                            </div>
                                        )}
                                        
                                        <Divider />
                                        
                                        {/* Tags Section */}
                                        <div className="flex flex-col gap-3">
                                            <Text weight="semibold" size={500}>
                                                Tags
                                            </Text>
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <Input
                                                    value={input}
                                                    placeholder="Enter new tag"
                                                    onChange={(e, data) => {
                                                        if (data) setInput(data.value);
                                                    }}
                                                    disabled={!props.user.is_admin}
                                                    className="flex-1"
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleAddTag(e);
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    disabled={disabled || !props.user.is_admin || !input.trim()}
                                                    size="medium"
                                                    icon={<Add20Regular />}
                                                    onClick={handleAddTag}
                                                    className="w-full sm:w-auto"
                                                >
                                                    Add Tag
                                                </Button>
                                            </div>
                                            {tags.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {tags.map((e, i) => {
                                                        return (
                                                            <Badge
                                                                key={`${e}_${i}`}
                                                                onClick={() => {
                                                                    if (props.user.is_admin) {
                                                                        setTags(tags.filter((tag) => tag !== e));
                                                                    }
                                                                }}
                                                                className="cursor-pointer"
                                                            >
                                                                {e}
                                                            </Badge>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <Divider />
                                        
                                        {/* Admins Section */}
                                        {props.user.is_admin && (
                                            <div className="flex flex-col gap-3">
                                                <Text weight="semibold" size={500}>
                                                    Administrators
                                                </Text>
                                                <Text size={300} className="text-(--colorNeutralForeground2)">
                                                    Select users who can manage this project
                                                </Text>
                                                {isLoadingUsers ? (
                                                    <Spinner size="small" />
                                                ) : (
                                                    <>
                                                        <Combobox
                                                            onOptionSelect={(_, data) => {
                                                                if (data.optionValue && !adminList.includes(data.optionValue)) {
                                                                    setAdminList([...adminList, data.optionValue]);
                                                                    setAdminInput("");
                                                                }
                                                            }}
                                                            placeholder="Search and select admin users..."
                                                            disabled={disabled}
                                                        >
                                                            {users
                                                                .filter((user) => user.id && !adminList.includes(user.id))
                                                                .map((user) => (
                                                                    <Option key={user.id} value={user.id!} text={`${user.full_name} (${user.email})`}>
                                                                        {user.full_name} ({user.email})
                                                                    </Option>
                                                                ))}
                                                        </Combobox>
                                                        {adminList.length > 0 && (
                                                            <div className="flex flex-wrap gap-2 mt-2">
                                                                {adminList.map((adminId) => {
                                                                    const adminUser = users.find((u) => u.id === adminId);
                                                                    return (
                                                                        <Badge
                                                                            key={adminId}
                                                                            onClick={() => {
                                                                                setAdminList(adminList.filter((id) => id !== adminId));
                                                                            }}
                                                                            className="cursor-pointer"
                                                                        >
                                                                            {adminUser ? `${adminUser.full_name}` : adminId}
                                                                            <span className="ml-1">×</span>
                                                                        </Badge>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        
                                        <Divider />
                                    </>
                                ) : null}
                                {/* Budget Section */}
                                <div className="flex flex-col gap-3">
                                    <Text weight="semibold" size={500}>
                                        Budget
                                    </Text>
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Field label="Total" className="flex-1">
                                            <Input
                                                value={budgetTotal?.toString() || ""}
                                                disabled={disabled || !props.user.is_admin}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                onChange={(e: any) => {
                                                    const value = e.target.value;
                                                    setBudgetTotal(value === "" ? null : parseFloat(value));
                                                }}
                                                placeholder="Enter budget total"
                                                className="w-full"
                                            />
                                        </Field>
                                        <Field label="Used" className="flex-1">
                                            <Input
                                                value={budgetUsed?.toFixed(2) || "0.00"}
                                                disabled
                                                placeholder="0.00"
                                                className="w-full"
                                            />
                                        </Field>
                                    </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-(--colorNeutralStroke1)">
                                    <Button
                                        appearance="secondary"
                                        id="button_cancel"
                                        type="reset"
                                        className="w-full sm:w-auto"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        appearance="primary"
                                        disabled={disabled || !canEditNote}
                                        id="button_submit"
                                        type="submit"
                                        className="w-full sm:w-auto sm:ml-auto"
                                    >
                                        Save Changes
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                        <DialogActions />
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            {showAlert && (
                <Dialog open={showAlert} onOpenChange={(_, data) => !data.open && setShowAlert(false)}>
                    <DialogSurface className="min-w-[90%] sm:min-w-[400px] max-w-[500px]">
                        <DialogBody>
                            <DialogTitle>Delete Project</DialogTitle>
                            <DialogContent>
                                <Text>Are you sure you want to delete this project? This action cannot be undone.</Text>
                            </DialogContent>
                            <DialogActions>
                                <Button appearance="secondary" onClick={handleAlertCancel}>
                                    Cancel
                                </Button>
                                <Button appearance="primary" onClick={handleAlertConfirm}>
                                    Delete
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            )}
        </>
    );
}
